import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { classNames } from 'primereact/utils';
import { Button } from 'primereact/button';
import 'primeicons/primeicons.css';
import './Sidebar.css';
import { useSidebar } from './SidebarContext';
import { clearAuth } from '../../utils/auth';

const topLevelItems = [
  { label: 'Dashboard', icon: 'pi pi-chart-line', path: '/dashboard' },
];

const menuGroups = [
  {
    id: 'content',
    label: 'Content',
    icon: 'pi pi-book',
    items: [
      { label: 'Entry Pages', icon: 'pi pi-book', path: '/entry_pages' },
      { label: 'Entry Pages locations', icon: 'pi pi-map', path: '/city_geos' },
      { label: 'Blogs', icon: 'pi pi-file', path: '/blogs' },
      { label: 'Country pages', icon: 'pi pi-globe', path: '/country_pages' },
      { label: 'Featured crossings', icon: 'pi pi-star', path: '/featured_crossings' },
      { label: 'Home city bar', icon: 'pi pi-compass', path: '/home_city_bar' },
      { label: 'Home pages', icon: 'pi pi-home', path: '/home_pages' },
      { label: 'Media kit', icon: 'pi pi-megaphone', path: '/media_kit' },
      { label: 'Download app', icon: 'pi pi-mobile', path: '/download_app' },
      { label: 'Home border states', icon: 'pi pi-flag', path: '/border_states' },
      { label: 'Global SEO', icon: 'pi pi-tags', path: '/metatags_manager' },
      { label: 'Word counter', icon: 'pi pi-calculator', path: '/word_counter' },
    ],
  },
  {
    id: 'community',
    label: 'Community & Mobile',
    icon: 'pi pi-users',
    items: [
      { label: 'News feed', icon: 'pi pi-megaphone', path: '/news_feed' },
      { label: 'Moderation', icon: 'pi pi-shield', path: '/community_moderation' },
      { label: 'Chat by crossing', icon: 'pi pi-comments', path: '/chat_moderation' },
      { label: 'Live tracks', icon: 'pi pi-send', path: '/live_tracks' },
      { label: 'Chat stickers', icon: 'pi pi-face-smile', path: '/chat_stickers' },
      { label: 'Publish as BorderGarita', icon: 'pi pi-verified', path: '/community_publish' },
      { label: 'Test push notifications', icon: 'pi pi-bell', path: '/push_test' },
      { label: 'Flush report images', icon: 'pi pi-trash', path: '/community_image_flush' },
      { label: 'Auto authorize', icon: 'pi pi-check-circle', path: '/auto_authorize_rules' },
      { label: 'Mobile users', icon: 'pi pi-user', path: '/mobile_users' },
      { label: 'App terms', icon: 'pi pi-file-edit', path: '/app_terms' },
      { label: 'Report points', icon: 'pi pi-trophy', path: '/report_points' },
      { label: 'Wait patterns', icon: 'pi pi-clock', path: '/wait_patterns' },
      { label: 'Wait times (ETA)', icon: 'pi pi-hourglass', path: '/wait_time_overrides' },
      { label: 'Flush wait times', icon: 'pi pi-trash', path: '/wait_times_flush' },
      { label: 'Entry heatmap', icon: 'pi pi-th-large', path: '/wait_pattern_heatmap' },
      { label: 'FB templates', icon: 'pi pi-facebook', path: '/social_prepubs' },
      { label: 'Clips studio', icon: 'pi pi-volume-up', path: '/clips_studio' },
      { label: 'Scheduled publications', icon: 'pi pi-calendar', path: '/scheduled_publications' },
    ],
  },
];

function pathMatches(pathname, itemPath) {
  if (!itemPath) return false;
  if (pathname === itemPath) return true;
  if (itemPath !== '/' && pathname.startsWith(`${itemPath}/`)) return true;
  if (itemPath === '/entry_pages' && pathname.startsWith('/entry_page')) return true;
  if (itemPath === '/blogs' && pathname.startsWith('/blogs/')) return true;
  if (itemPath === '/country_pages' && pathname.startsWith('/country_pages/')) return true;
  if (itemPath === '/city_geos' && pathname.startsWith('/city_geos/')) return true;
  if (itemPath === '/community_moderation' && pathname.startsWith('/community_moderation/')) return true;
  if (itemPath === '/chat_moderation' && pathname.startsWith('/chat_moderation')) return true;
  if (itemPath === '/community_publish' && pathname.startsWith('/community_publish')) return true;
  if (itemPath === '/push_test' && pathname.startsWith('/push_test')) return true;
  if (itemPath === '/community_image_flush' && pathname.startsWith('/community_image_flush')) return true;
  if (itemPath === '/mobile_users' && pathname.startsWith('/mobile_users/')) return true;
  if (itemPath === '/app_terms' && pathname.startsWith('/app_terms')) return true;
  if (itemPath === '/news_feed' && pathname.startsWith('/news_feed')) return true;
  if (itemPath === '/admin_users' && pathname.startsWith('/admin_users/')) return true;
  return false;
}

function groupContainsPath(group, pathname) {
  return group.items.some((item) => pathMatches(pathname, item.path));
}

export default function AppSidebar() {
  const location = useLocation();
  const pathname = location.pathname;
  const { collapsed, mobileOpen, closeMobile, toggleCollapsed } = useSidebar();

  const activeGroupIds = useMemo(
    () => menuGroups.filter((g) => groupContainsPath(g, pathname)).map((g) => g.id),
    [pathname]
  );

  const [openGroups, setOpenGroups] = useState(() => new Set(activeGroupIds));

  useEffect(() => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      activeGroupIds.forEach((id) => next.add(id));
      return next;
    });
  }, [activeGroupIds]);

  const toggleGroup = (id) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const user = (() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })();

  return (
    <aside
      className={classNames('app-sidebar', {
        collapsed,
        'mobile-open': mobileOpen,
      })}
    >
      <div className="app-header">
        <div className="sidebar-logo-wrap">
          <img src="/logo.png" alt="Logo" className="sidebar-logo-full" />
          <img src="/logo192_v2.png" alt="Logo" className="sidebar-logo-mark" />
        </div>
        <Button
          type="button"
          icon="pi pi-times"
          className="p-button-text p-button-rounded sidebar-close-mobile"
          onClick={closeMobile}
          aria-label="Close menu"
        />
      </div>

      <nav className="sidebar-nav">
        <ul className="sidebar-top-items">
          {topLevelItems.map((item) => (
            <li
              key={item.path}
              className={classNames({ active: pathMatches(pathname, item.path) })}
            >
              <Link to={item.path} title={item.label} onClick={closeMobile}>
                <i className={item.icon} />
                <span>{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>

        {menuGroups.map((group) => {
          const isOpen = openGroups.has(group.id) || collapsed;
          const hasActive = groupContainsPath(group, pathname);

          return (
            <div
              key={group.id}
              className={classNames('sidebar-group', { open: isOpen, 'has-active': hasActive })}
            >
              <button
                type="button"
                className="sidebar-group-toggle"
                onClick={() => toggleGroup(group.id)}
                aria-expanded={isOpen}
                title={group.label}
              >
                <span className="sidebar-group-toggle-left">
                  <i className={group.icon} />
                  <span>{group.label}</span>
                </span>
                <i className={classNames('pi sidebar-group-chevron', isOpen ? 'pi-chevron-down' : 'pi-chevron-right')} />
              </button>

              {isOpen && (
                <ul className="sidebar-group-items">
                  {group.items.map((item) => (
                    <li
                      key={item.path}
                      className={classNames({ active: pathMatches(pathname, item.path) })}
                    >
                      <Link to={item.path} title={item.label} onClick={closeMobile}>
                        <i className={item.icon} />
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}

        <div className="sidebar-group account-group open">
          <p className="section-title">Account</p>
          <ul className="sidebar-group-items">
            {user && (
              <li className="sidebar-user">
                <div className="sidebar-user-row" title={user.name || user.username || 'User'}>
                  <i className="pi pi-user" />
                  <span>
                    <strong>{user.name || 'User'}</strong>
                    {user.username ? <small>{user.username}</small> : null}
                  </span>
                </div>
              </li>
            )}
            <li className={classNames({ active: pathMatches(pathname, '/settings') })}>
              <Link to="/settings" title="Settings" onClick={closeMobile}>
                <i className="pi pi-cog" />
                <span>Settings</span>
              </Link>
            </li>
            <li className={classNames({ active: pathMatches(pathname, '/story_reports') })}>
              <Link to="/story_reports" title="Reportes de historias" onClick={closeMobile}>
                <i className="pi pi-flag" />
                <span>Reportes de historias</span>
              </Link>
            </li>
            <li className={classNames({ active: pathMatches(pathname, '/admin_users') })}>
              <Link to="/admin_users" title="Admin users" onClick={closeMobile}>
                <i className="pi pi-id-card" />
                <span>Admin users</span>
              </Link>
            </li>
            <li>
              <button
                type="button"
                title="Log out"
                className="sidebar-link-btn"
                onClick={() => {
                  closeMobile();
                  clearAuth();
                  window.location.href = '/login';
                }}
              >
                <i className="pi pi-sign-out" />
                <span>Log out</span>
              </button>
            </li>
          </ul>
        </div>
      </nav>

      <div className="sidebar-footer">
        <Button
          type="button"
          icon={collapsed ? 'pi pi-angle-double-right' : 'pi pi-angle-double-left'}
          className="p-button-text p-button-rounded sidebar-collapse-btn"
          onClick={toggleCollapsed}
          tooltip={collapsed ? 'Expand' : 'Collapse'}
          tooltipOptions={{ position: 'right' }}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        />
      </div>
    </aside>
  );
}
